import React, {FC, useMemo} from 'react';
import {CellProps, Column} from 'react-table';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {isDefined} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {ConversationSortPropertiesEnum} from '../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {inboxHasColor} from '../../../../../libs/shared/core/src/models/helpers/inboxHelpers';
import {AppContentTypesEnum} from '../../../../../libs/shared/core/src/store/panels/app/commonAppPanelState';
import {ColorDot} from '../../../../../libs/shared/ui/src/components/utility/colorDot';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {LiveDashboardContentInboxWorkloadModel} from '../../../../models/api/liveDashboardContentModel';
import {CellsSortDirectionsEnum} from '../../../../models/api/sortOptionModel';
import {InboxesFiltersEnum} from '../../../../models/url/models/inboxesUrlModel';
import {makeGetInbox} from '../../../../store/data/inboxes/inboxesSelectors';
import {pushParametersAndFocusAction} from '../../../../store/panels/app/appPanelRouteActions';
import {InboxesParametersTypesEnum} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {emptyInboxesSelection} from '../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {getFirstAppPanelId} from '../../../../store/panels/panelsSelectors';
import {renderDurationValue} from '../../../helpers/metricRenderers/durationMetricRenderer';
import {renderNumericalValue} from '../../../helpers/metricRenderers/numericalMetricRenderer';
import {ReportElementIdsEnum} from '../../../helpers/reportHelpers/reportTypes';
import {ViewInInbox} from '../viewInInbox';

const InboxRowDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
`;

const StyledInboxNameSpan = styled.span`
  ${ellipsis()};
`;

const InboxNameCell: FC<{value: number}> = ({value}) => {
  const getInbox = useMemo(() => makeGetInbox<void>(() => value), [value]);
  const inbox = useAppSelector(getInbox);

  if (!inbox) {
    return (
      <InboxRowDiv>
        <ColorDot color="gray" size={8} />
        <i>Inbox</i>
      </InboxRowDiv>
    );
  }

  return (
    <InboxRowDiv>
      <ColorDot color={inboxHasColor(inbox) ? inbox.color : 'gray'} size={8} />
      <StyledInboxNameSpan>{inbox.name}</StyledInboxNameSpan>
    </InboxRowDiv>
  );
};

const ConversationCount = ({value}: {value: number}) => renderNumericalValue({value, isZeroValid: true});

const OpenCountCell: FC<CellProps<LiveDashboardContentInboxWorkloadModel>> = ({value, row}) => {
  const appPanelId = useAppSelector(getFirstAppPanelId);

  return (
    <ViewInInbox
      actionBuilder={() =>
        pushParametersAndFocusAction(appPanelId, {
          contentType: AppContentTypesEnum.INBOXES,
          parameters: {
            type: InboxesParametersTypesEnum.INBOX,
            inboxId: row.original.inboxId,
            sortDirection: CellsSortDirectionsEnum.DESCENDING,
            sortProperty: ConversationSortPropertiesEnum.BUMPED_AT,
            selection: emptyInboxesSelection,
            refinement: undefined,
            filters: [InboxesFiltersEnum.UNASSIGNED, InboxesFiltersEnum.ASSIGNED],
          },
        })
      }
      trackingPayload={{
        report_section: ReportElementIdsEnum.LIVE_DASHBOARD_WORKLOAD_BY_INBOX_TABLE,
        resource_type: 'inbox',
        resource_id: row.original.inboxId,
        column: 'open',
        clicked_value: row.original.openCount,
      }}
    >
      <ConversationCount value={value} />
    </ViewInInbox>
  );
};

const UnassignedCountCell: FC<CellProps<LiveDashboardContentInboxWorkloadModel>> = ({value, row}) => {
  const appPanelId = useAppSelector(getFirstAppPanelId);

  return (
    <ViewInInbox
      actionBuilder={() =>
        pushParametersAndFocusAction(appPanelId, {
          contentType: AppContentTypesEnum.INBOXES,
          parameters: {
            type: InboxesParametersTypesEnum.INBOX,
            inboxId: row.original.inboxId,
            sortDirection: CellsSortDirectionsEnum.DESCENDING,
            sortProperty: ConversationSortPropertiesEnum.BUMPED_AT,
            selection: emptyInboxesSelection,
            refinement: undefined,
            filters: [InboxesFiltersEnum.UNASSIGNED],
          },
        })
      }
      trackingPayload={{
        report_section: ReportElementIdsEnum.LIVE_DASHBOARD_WORKLOAD_BY_INBOX_TABLE,
        resource_type: 'inbox',
        resource_id: row.original.inboxId,
        column: 'unassigned',
        clicked_value: value,
      }}
    >
      <ConversationCount value={value} />
    </ViewInInbox>
  );
};

const StyledRowReverseDiv = styled.div`
  display: flex;
  flex-direction: row-reverse;
`;

const WaitingCountCell: FC<CellProps<LiveDashboardContentInboxWorkloadModel>> = ({value, row}) => {
  const {inboxId} = row.original;

  const getInbox = useMemo(() => makeGetInbox<void>(() => inboxId), [inboxId]);
  const inbox = useAppSelector(getInbox);
  const appPanelId = useAppSelector(getFirstAppPanelId);

  if (!isDefined(value)) {
    return <StyledRowReverseDiv>-</StyledRowReverseDiv>;
  }

  return (
    <ViewInInbox
      actionBuilder={() =>
        pushParametersAndFocusAction(appPanelId, {
          contentType: AppContentTypesEnum.INBOXES,
          parameters: {
            type: InboxesParametersTypesEnum.INBOX,
            inboxId,
            sortDirection: CellsSortDirectionsEnum.DESCENDING,
            sortProperty: ConversationSortPropertiesEnum.BUMPED_AT,
            selection: emptyInboxesSelection,
            refinement: undefined,
            filters: [inbox?.isTicketingEnabled ? InboxesFiltersEnum.WAITING : InboxesFiltersEnum.SNOOZED],
          },
        })
      }
      trackingPayload={{
        report_section: ReportElementIdsEnum.LIVE_DASHBOARD_WORKLOAD_BY_INBOX_TABLE,
        resource_type: 'inbox',
        resource_id: inboxId,
        column: 'waiting',
        clicked_value: value,
      }}
    >
      <ConversationCount value={value} />
    </ViewInInbox>
  );
};

const OldestOpenCell: FC<CellProps<LiveDashboardContentInboxWorkloadModel>> = ({value, row}) => {
  const appPanelId = useAppSelector(getFirstAppPanelId);

  if (!isDefined(value)) {
    return renderDurationValue({value: 0});
  }

  return (
    <ViewInInbox
      actionBuilder={() =>
        pushParametersAndFocusAction(appPanelId, {
          contentType: AppContentTypesEnum.INBOXES,
          parameters: {
            type: InboxesParametersTypesEnum.INBOX,
            inboxId: row.original.inboxId,
            sortDirection: CellsSortDirectionsEnum.ASCENDING,
            sortProperty: ConversationSortPropertiesEnum.WAITING_SINCE,
            selection: emptyInboxesSelection,
            refinement: undefined,
            filters: [InboxesFiltersEnum.UNASSIGNED, InboxesFiltersEnum.ASSIGNED],
          },
        })
      }
      trackingPayload={{
        report_section: ReportElementIdsEnum.LIVE_DASHBOARD_WORKLOAD_BY_INBOX_TABLE,
        resource_type: 'inbox',
        resource_id: row.original.inboxId,
        column: 'oldest',
        clicked_value: row.original.oldestUnrepliedTimestamp,
      }}
    >
      {renderDurationValue({
        value: (new Date().getTime() - value) / 1000,
        isCompact: true,
      })}
    </ViewInInbox>
  );
};

const inboxNameColumn = {
  id: 'inbox',
  Header: <Trans id="live-dashboard-workload-by-inbox-inbox-column">Inboxes</Trans>,
  accessor: 'inboxId',
  Cell: InboxNameCell,
  disableSortBy: true,
  minWidth: 190,
  maxWidth: 190,
  width: '100%',
} as const;

const openCountColumn = {
  id: 'open',
  Header: <Trans id="live-dashboard-workload-by-inbox-open-column">Open</Trans>,
  accessor: 'openCount',
  width: 74,
  Cell: OpenCountCell,
} as const;

const unassignedCountColumn = {
  id: 'unassigned',
  Header: <Trans id="live-dashboard-workload-by-inbox-unassigned-column">Unassigned</Trans>,
  accessor: 'unassignedCount',
  width: 112,
  Cell: UnassignedCountCell,
} as const;

const oldestOpenTimestampColumn = {
  id: 'oldestUnrepliedDuration',
  Header: <Trans id="live-dashboard-workload-by-inbox-oldest-open-column">Oldest open</Trans>,
  accessor: 'oldestUnrepliedTimestamp',
  width: 140,
  Cell: OldestOpenCell,
} as const;

// When including waiting conversations, the oldest conversation is not necessarily open.
const oldestTimestampColumn = {
  ...oldestOpenTimestampColumn,
  Header: <Trans id="live-dashboard-workload-by-inbox-oldest-column">Oldest</Trans>,
} as const;

const waitingCountColumn = {
  id: 'waiting',
  Header: <Trans id="live-dashboard-workload-by-inbox-waiting-column">Waiting</Trans>,
  accessor: 'waitingCount',
  width: 74,
  Cell: WaitingCountCell,
} as const;

export const liveDashboardWorkloadByInboxColumns: Array<Column<LiveDashboardContentInboxWorkloadModel>> = [
  inboxNameColumn,
  openCountColumn,
  unassignedCountColumn,
  oldestOpenTimestampColumn,
] as const;

export const liveDashboardWorkloadByInboxColumnsWithWaiting: Array<
  Column<LiveDashboardContentInboxWorkloadModel>
> = [
  inboxNameColumn,
  openCountColumn,
  waitingCountColumn,
  unassignedCountColumn,
  oldestTimestampColumn,
] as const;
